import React from 'react';
import {
  Box,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { grey } from '@mui/material/colors';
import { SearchOff } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';
import { generateProposalPageURL } from 'utils/generateProposalPageURL';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { generateColumns } from './tableConfig';
import { PartnerProposalDashboardEntity } from './types';

interface Props {
  tableData: PartnerProposalDashboardEntity[];
  isLoading: boolean;
  total: number;
  sorting: any;
  pagination: any;
}

export const PartnerProposalsDashboardTable = ({ tableData, isLoading, total, sorting, pagination }: Props) => {
  const classes = useStyles();

  const handleGoToProposalDetail = (proposal: PartnerProposalDashboardEntity) => {
    if (!proposal.dynamicsAccountId) return;
    const url = generateProposalPageURL(proposal.id, proposal.dynamicsAccountId);
    window.open(url, '_blank');
  };

  const handleGoToProposal = (proposal: PartnerProposalDashboardEntity) => {
    window.open(`/proposalDetail/${proposal.id}?from=/proposals`, '_blank');
  };

  const columns = generateColumns(handleGoToProposalDetail, handleGoToProposal);

  return (
    <Stack sx={{ height: 'calc(100% - 14.5rem)', minHeight: 'calc(100% - 14.5rem)' }}>
      <TableContainer component={Paper} className={classes.container}>
        {isLoading && !!tableData.length && <LinearProgress color="primary" className={classes.loading} />}

        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell
                  align="center"
                  key={column.renderKey}
                  sx={{ backgroundColor: grey[50] }}
                  sortDirection={sorting.value.key === column.sortKey ? sorting.value.value : false}
                >
                  {column?.sortKey ? (
                    <TableSortLabel
                      active={sorting.value.key === column.renderKey}
                      direction={sorting.value.key === column.sortKey ? sorting.value.value : 'asc'}
                      onClick={() =>
                        sorting.onChange({
                          key: column.sortKey!,
                          value: sorting.value.value === 'asc' ? 'desc' : 'asc',
                        })
                      }
                    >
                      {column.label}
                      {sorting.value.key === column.sortKey && (
                        <Box component="span" sx={visuallyHidden}>
                          {sorting.value.value === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      )}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            <RenderWithFallback
              isLoading={!tableData.length && isLoading}
              fallBack={
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <Stack direction="row" alignItems="center" justifyContent="center" gap="1rem">
                      <CircularProgress size={20} /> <Typography>Loading...</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              }
            >
              <RenderWithCondition
                condition={!!tableData.length}
                fallBack={
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <Stack direction="row" alignItems="center" justifyContent="center" gap="1rem">
                        <SearchOff />
                        <Typography>No data</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                }
              >
                {tableData.map(proposal => (
                  <TableRow key={proposal.id}>
                    {columns.map(({ label, bodyFormatter, renderKey }) => (
                      <TableCell key={`${label}_${renderKey}`} align="center">
                        {typeof bodyFormatter === 'function'
                          ? bodyFormatter(proposal[renderKey], proposal)
                          : (proposal[renderKey] ?? '-')}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </RenderWithCondition>
            </RenderWithFallback>
          </TableBody>
        </Table>
      </TableContainer>

      <Paper>
        <TablePagination
          component="div"
          colSpan={columns.length}
          count={total}
          page={pagination.value.page - 1}
          rowsPerPageOptions={[5, 15, 25]}
          rowsPerPage={pagination.value.perPage}
          onRowsPerPageChange={e => pagination.onChange({ page: 1, perPage: parseInt(e.target.value, 10) })}
          onPageChange={(_, newPage) => pagination.onChange((prev: any) => ({ ...prev, page: newPage + 1 }))}
        />
      </Paper>
    </Stack>
  );
};

const useStyles = makeStyles({
  container: {
    height: '100%',
    position: 'relative',
  },
  loading: {
    top: 0,
    left: 0,
    height: 2,
    zIndex: 3,
    width: '100%',
    position: 'absolute',
  },
});
